import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle2, XCircle, AlertCircle, Clock, RefreshCw, 
  Settings, Eye, Play, ChevronDown, ChevronRight,
  Truck, ShoppingBag, CreditCard, DollarSign, User, Phone, MapPin,
  FileText, List, ClipboardCheck, Ban, Package, Zap, Terminal,
  AlertTriangle, Info, Copy, Printer, Hash
} from 'lucide-react';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import apiClient from '../lib/api';

// ==================== COMPONENTES AUXILIARES ====================

const StatusBadge = ({ status, size = 'md' }) => {
  const configs = {
    PASS: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'PASS' },
    FAIL: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'FAIL' },
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'PENDENTE' },
    RUNNING: { bg: 'bg-blue-100', text: 'text-blue-700', icon: RefreshCw, label: 'TESTANDO' },
  };
  const c = configs[status] || configs.PENDING;
  const Icon = c.icon;
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 ${c.bg} ${c.text} ${sizeClasses} rounded-full font-medium`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {c.label}
    </span>
  );
};

const EventStatusBadge = ({ status }) => {
  const configs = {
    PROCESSED: { bg: 'bg-green-100', text: 'text-green-700', label: 'OK' },
    IGNORED_DUPLICATE: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Duplicado' },
    FAILED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Falhou' },
    RECEIVED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Recebido' },
    QUEUED: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Na Fila' },
  };
  const c = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  
  return (
    <span className={`inline-flex items-center ${c.bg} ${c.text} px-2 py-0.5 rounded text-xs font-medium`}>
      {c.label}
    </span>
  );
};

const OrderStatusBadge = ({ status }) => {
  const configs = {
    'PLACED': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Novo' },
    'CONFIRMED': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmado' },
    'PREPARATION_STARTED': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Em Preparo' },
    'READY_TO_PICKUP': { bg: 'bg-green-100', text: 'text-green-800', label: 'Pronto' },
    'DISPATCHED': { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Despachado' },
    'DELIVERED': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Entregue' },
    'CONCLUDED': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Concluído' },
    'CANCELLED': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
    'CANCELLATION_REQUESTED': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Cancelamento Solicitado' },
  };
  const c = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  
  return (
    <span className={`inline-flex items-center ${c.bg} ${c.text} px-2 py-1 rounded-full text-xs font-medium`}>
      {c.label}
    </span>
  );
};

const TokenTimer = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const updateTimer = () => {
      if (!expiresAt) {
        setTimeLeft('N/A');
        return;
      }
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeLeft('Expirado');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${hours}h ${minutes}min`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  const isExpiringSoon = timeLeft && !timeLeft.includes('N/A') && !timeLeft.includes('Expirado') && 
    parseInt(timeLeft) < 1;
  
  return (
    <span className={`font-mono ${isExpiringSoon ? 'text-orange-600' : 'text-gray-600'}`}>
      {timeLeft}
    </span>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

const IFoodHomologationPage = () => {
  const [storeId, setStoreId] = useState(null);
  const [stores, setStores] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('checklist');
  
  // Data states
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [cancellationHistory, setCancellationHistory] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // UI states
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  
  // Checklist states
  const [checklistItems, setChecklistItems] = useState([
    { id: 'webhook_receive', label: 'Recebe eventos via webhook com sucesso', status: 'PENDING', lastTest: null },
    { id: 'confirm_delivery_immediate', label: 'Confirmar DELIVERY IMMEDIATE', status: 'PENDING', lastTest: null },
    { id: 'confirm_delivery_scheduled', label: 'Confirmar DELIVERY SCHEDULED (exibe data/hora)', status: 'PENDING', lastTest: null },
    { id: 'cancel_with_reasons', label: 'Cancelar DELIVERY com motivos (cancellationReasons)', status: 'PENDING', lastTest: null },
    { id: 'confirm_takeout_ready', label: 'Confirmar TAKEOUT e marcar "pronto"', status: 'PENDING', lastTest: null },
    { id: 'show_card_payment', label: 'Mostrar pagamento cartão (bandeira)', status: 'PENDING', lastTest: null },
    { id: 'show_cash_payment', label: 'Mostrar pagamento dinheiro + troco', status: 'PENDING', lastTest: null },
    { id: 'show_coupons', label: 'Mostrar cupons e subsidiador (iFood/Loja)', status: 'PENDING', lastTest: null },
    { id: 'show_item_notes', label: 'Mostrar observações dos itens', status: 'PENDING', lastTest: null },
    { id: 'update_external_cancel', label: 'Atualizar status se cancelado pelo cliente/iFood', status: 'PENDING', lastTest: null },
    { id: 'update_external_change', label: 'Atualizar status se outro app alterar (Gestor)', status: 'PENDING', lastTest: null },
    { id: 'dedupe_events', label: 'Deduplicar evento repetido', status: 'PENDING', lastTest: null },
    { id: 'show_document', label: 'Mostrar CPF/CNPJ quando obrigatório', status: 'PENDING', lastTest: null },
    { id: 'process_handshake', label: 'Processar eventos da negociação (handshake)', status: 'PENDING', lastTest: null },
    { id: 'show_pickup_code', label: 'Mostrar código de coleta', status: 'PENDING', lastTest: null },
    { id: 'token_renewal', label: 'Token renova apenas perto de expirar', status: 'PENDING', lastTest: null },
    { id: 'rate_limit', label: 'Rate limit respeitado', status: 'PENDING', lastTest: null },
  ]);

  // Load stores on mount
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await apiClient.get('/rbac/stores');
        setStores(res.data || []);
        if (res.data?.length > 0) {
          setStoreId(res.data[0].id);
        }
      } catch (err) {
        toast.error('Erro ao carregar lojas');
      }
    };
    loadStores();
  }, []);

  // Load config when store changes
  useEffect(() => {
    if (storeId) {
      loadConfig();
      loadHomologationStatus();
    }
  }, [storeId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/ifood/config/${storeId}`);
      setConfig(res.data);
    } catch (err) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  // Carrega status do checklist do backend
  const loadHomologationStatus = async () => {
    if (!storeId) return;
    try {
      const res = await apiClient.get(`/ifood/homologation/status/${storeId}`);
      if (res.data?.checklist) {
        // Mapear IDs do backend para o frontend
        const backendToFrontendMap = {
          'webhook_receive': 'webhook_receive',
          'confirm_delivery_immediate': 'confirm_delivery_immediate',
          'confirm_delivery_scheduled': 'confirm_delivery_scheduled',
          'cancel_with_reasons': 'cancel_with_reasons',
          'confirm_takeout': 'confirm_takeout_ready',
          'dedupe_events': 'dedupe_events',
          'token_renewal': 'token_renewal',
        };
        
        setChecklistItems(prev => prev.map(item => {
          const backendId = Object.keys(backendToFrontendMap).find(
            key => backendToFrontendMap[key] === item.id
          );
          if (backendId && res.data.checklist[backendId]) {
            return {
              ...item,
              status: res.data.checklist[backendId].status,
              details: res.data.checklist[backendId].details
            };
          }
          return item;
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar status de homologação:', err);
    }
  };

  const loadWebhookEvents = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await apiClient.get(`/ifood/homologation/webhook-events/${storeId}?limit=100`);
      setWebhookEvents(res.data || []);
    } catch (err) {
      console.error('Error loading webhook events:', err);
    }
  }, [storeId]);

  const loadOrders = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await apiClient.get(`/ifood/orders/${storeId}`);
      setOrders(res.data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  }, [storeId]);

  useEffect(() => {
    if (storeId && activeTab === 'events') {
      loadWebhookEvents();
    } else if (storeId && (activeTab === 'orders' || activeTab === 'takeout')) {
      loadOrders();
    }
  }, [storeId, activeTab, loadWebhookEvents, loadOrders]);

  // Test credentials
  const handleTestCredentials = async () => {
    setTestingCredentials(true);
    try {
      const res = await apiClient.post(`/ifood/config/${storeId}/authenticate`);
      toast.success('Credenciais válidas! Token obtido com sucesso.');
      loadConfig();
      updateChecklistItem('token_renewal', 'PASS');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha na autenticação');
      updateChecklistItem('token_renewal', 'FAIL');
    } finally {
      setTestingCredentials(false);
    }
  };

  // Test webhook
  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await apiClient.post(`/ifood/homologation/test-webhook/${storeId}`);
      if (res.data.success) {
        toast.success('Webhook funcionando! Resposta 202 em tempo adequado.');
        updateChecklistItem('webhook_receive', 'PASS');
      } else {
        toast.error('Webhook falhou no teste');
        updateChecklistItem('webhook_receive', 'FAIL');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao testar webhook');
      updateChecklistItem('webhook_receive', 'FAIL');
    } finally {
      setTestingWebhook(false);
    }
  };

  // Load cancellation reasons for an order
  const handleLoadCancellationReasons = async (orderId) => {
    setLoadingReasons(true);
    setOrderToCancel(orderId);
    try {
      const res = await apiClient.get(`/ifood/orders/${storeId}/${orderId}/cancellation-reasons`);
      setCancellationReasons(res.data.cancellationReasons || []);
      setShowCancelModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar motivos de cancelamento');
    } finally {
      setLoadingReasons(false);
    }
  };

  // Cancel order with reason
  const handleCancelOrder = async () => {
    if (!selectedReason || !orderToCancel) {
      toast.error('Selecione um motivo de cancelamento');
      return;
    }
    
    setCancelling(true);
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderToCancel}/cancel`, {
        cancellationCode: selectedReason.cancelCodeId
      });
      toast.success('Pedido cancelado com sucesso!');
      setShowCancelModal(false);
      setSelectedReason(null);
      setOrderToCancel(null);
      loadOrders();
      updateChecklistItem('cancel_with_reasons', 'PASS');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar pedido');
    } finally {
      setCancelling(false);
    }
  };

  // Confirm order
  const handleConfirmOrder = async (order) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${order.ifood_order_id}/confirm`);
      toast.success('Pedido confirmado!');
      loadOrders();
      
      // Update checklist based on order type
      if (order.order_timing === 'SCHEDULED') {
        updateChecklistItem('confirm_delivery_scheduled', 'PASS');
      } else {
        updateChecklistItem('confirm_delivery_immediate', 'PASS');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar pedido');
    }
  };

  // Dispatch order
  const handleDispatchOrder = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/dispatch`);
      toast.success('Pedido despachado!');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao despachar pedido');
    }
  };

  // Handler inteligente: DELIVERY vai direto para dispatch, TAKEOUT usa readyToPickup
  const handleMarkReady = async (orderId, orderType) => {
    try {
      if (orderType === 'DELIVERY') {
        await apiClient.post(`/ifood/orders/${storeId}/${orderId}/dispatch`);
        toast.success('Pedido despachado para entrega!');
        updateChecklistItem('dispatch_order', 'PASS');
      } else {
        await apiClient.post(`/ifood/orders/${storeId}/${orderId}/ready-to-pickup`);
        toast.success('Pedido pronto para retirada!');
        updateChecklistItem('confirm_takeout_ready', 'PASS');
      }
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao marcar pedido');
    }
  };

  // Ready to pickup (TAKEOUT)
  const handleReadyToPickup = async (orderId) => {
    try {
      await apiClient.post(`/ifood/orders/${storeId}/${orderId}/ready-to-pickup`);
      toast.success('Pedido marcado como pronto!');
      loadOrders();
      updateChecklistItem('confirm_takeout_ready', 'PASS');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  // Reprocess event
  const handleReprocessEvent = async (eventId) => {
    try {
      await apiClient.post(`/ifood/homologation/reprocess-event/${eventId}`);
      toast.success('Evento reenviado para processamento');
      loadWebhookEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reprocessar evento');
    }
  };

  // Update status from iFood
  const handleRefreshOrderStatus = async (orderId) => {
    try {
      const res = await apiClient.get(`/ifood/orders/${storeId}/${orderId}/refresh`);
      toast.success('Status atualizado!');
      loadOrders();
      if (res.data.status_changed) {
        updateChecklistItem('update_external_change', 'PASS');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const updateChecklistItem = (id, status) => {
    setChecklistItems(prev => prev.map(item => 
      item.id === id ? { ...item, status, lastTest: new Date().toISOString() } : item
    ));
  };

  // Executa teste individual de um item do checklist
  const runSingleCheck = async (itemId) => {
    updateChecklistItem(itemId, 'RUNNING');
    
    try {
      // Carregar dados necessários para validação
      const [statusRes, eventsRes, ordersRes] = await Promise.all([
        apiClient.get(`/ifood/homologation/status/${storeId}`).catch(() => ({ data: {} })),
        apiClient.get(`/ifood/homologation/webhook-events/${storeId}?limit=100`).catch(() => ({ data: [] })),
        apiClient.get(`/ifood/orders/${storeId}`).catch(() => ({ data: [] }))
      ]);
      
      const stats = statusRes.data?.stats || {};
      const events = eventsRes.data || [];
      const ordersList = ordersRes.data || [];
      
      let passed = false;
      
      switch (itemId) {
        case 'webhook_receive':
          // Verifica se tem eventos processados
          passed = stats.processed_events > 0 || events.some(e => e.status === 'PROCESSED');
          break;
          
        case 'confirm_delivery_immediate':
          // Verifica se tem pedidos DELIVERY IMMEDIATE confirmados
          passed = ordersList.some(o => 
            o.order_type === 'DELIVERY' && 
            o.order_timing === 'IMMEDIATE' && 
            ['CONFIRMED', 'PREPARATION_STARTED', 'READY_TO_PICKUP', 'DISPATCHED', 'DELIVERED', 'CONCLUDED'].includes(o.ifood_status)
          );
          break;
          
        case 'confirm_delivery_scheduled':
          // Verifica se tem pedidos DELIVERY SCHEDULED
          passed = ordersList.some(o => 
            o.order_type === 'DELIVERY' && 
            o.order_timing === 'SCHEDULED'
          );
          break;
          
        case 'cancel_with_reasons':
          // Verifica se tem pedidos cancelados
          passed = ordersList.some(o => o.ifood_status === 'CANCELLED');
          break;
          
        case 'confirm_takeout_ready':
          // Verifica se tem pedidos TAKEOUT
          passed = ordersList.some(o => o.order_type === 'TAKEOUT');
          break;
          
        case 'show_card_payment':
          // Verifica se tem pedidos com pagamento cartão
          passed = ordersList.some(o => 
            o.payment_method === 'CREDIT' || 
            o.payment_method === 'DEBIT' ||
            o.payments?.some(p => p.type === 'CREDIT' || p.type === 'DEBIT')
          );
          break;
          
        case 'show_cash_payment':
          // Verifica se tem pedidos com pagamento dinheiro
          passed = ordersList.some(o => 
            o.payment_method === 'CASH' ||
            o.payments?.some(p => p.type === 'CASH')
          );
          break;
          
        case 'show_coupons':
          // Verifica se tem pedidos com cupons/benefícios
          passed = ordersList.some(o => 
            o.benefits?.length > 0 || 
            o.total_discount > 0
          );
          break;
          
        case 'show_item_notes':
          // Verifica se tem pedidos com observações nos itens
          passed = ordersList.some(o => 
            o.items?.some(i => i.observations || i.options?.length > 0)
          );
          break;
          
        case 'dedupe_events':
          // Verifica se tem eventos duplicados detectados
          passed = stats.duplicate_events > 0 || events.some(e => e.status === 'IGNORED_DUPLICATE');
          break;
          
        case 'token_renewal':
          // Verifica se token está válido
          passed = config?.auth_status === 'AUTHORIZED';
          break;
          
        case 'update_external_cancel':
        case 'update_external_change':
          // Verifica se tem eventos de mudança externa
          passed = events.some(e => 
            e.event_type === 'STATUS_CHANGED' || 
            e.event_code === 'CAN' ||
            e.event_code === 'CFM'
          );
          break;
          
        case 'show_document':
          // Verifica se tem pedidos com CPF/CNPJ
          passed = ordersList.some(o => o.customer?.document_number);
          break;
          
        case 'process_handshake':
          // Verifica se tem eventos de handshake
          passed = events.some(e => e.event_code === 'DDCR' || e.event_type === 'DELIVERY_DATE_CHANGE_REQUEST');
          break;
          
        case 'show_pickup_code':
          // Verifica se tem pedidos com código de coleta
          passed = ordersList.some(o => o.pickup_code || o.order_type === 'TAKEOUT');
          break;
          
        case 'rate_limit':
          // Assumir que está ok se não houve erros 429
          passed = true;
          break;
          
        default:
          passed = false;
      }
      
      updateChecklistItem(itemId, passed ? 'PASS' : 'PENDING');
      
    } catch (err) {
      console.error(`Erro ao testar ${itemId}:`, err);
      updateChecklistItem(itemId, 'FAIL');
    }
  };

  const runAllChecks = async () => {
    // Executar todos os testes
    for (const item of checklistItems) {
      await runSingleCheck(item.id);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    toast.success('Todos os testes executados!');
  };

  // Calculate checklist stats
  const checklistStats = {
    total: checklistItems.length,
    passed: checklistItems.filter(i => i.status === 'PASS').length,
    failed: checklistItems.filter(i => i.status === 'FAIL').length,
    pending: checklistItems.filter(i => i.status === 'PENDING').length,
  };

  const tabs = [
    { id: 'checklist', label: 'Checklist', icon: ClipboardCheck },
    { id: 'config', label: 'Configuração', icon: Settings },
    { id: 'events', label: 'Monitor de Eventos', icon: Zap },
    { id: 'orders', label: 'Pedidos iFood', icon: Package },
    { id: 'cancel', label: 'Cancelamento', icon: Ban },
    { id: 'takeout', label: 'Takeout / Coleta', icon: ShoppingBag },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="ifood-homologation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Homologação iFood</h1>
            <p className="text-gray-500 mt-1">Checklist e testes para validação da integração</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={storeId || ''}
              onChange={(e) => setStoreId(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white text-gray-700"
            >
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
            {config?.configured && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Token expira em:</span>
                <TokenTimer expiresAt={config.token_expires_at} />
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progresso da Homologação</span>
            <span className="text-sm text-gray-500">
              {checklistStats.passed}/{checklistStats.total} itens aprovados
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(checklistStats.passed / checklistStats.total) * 100}%` }}
            />
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="w-3 h-3" /> {checklistStats.passed} aprovados
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <XCircle className="w-3 h-3" /> {checklistStats.failed} falharam
            </span>
            <span className="flex items-center gap-1 text-yellow-600">
              <Clock className="w-3 h-3" /> {checklistStats.pending} pendentes
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border">
          <div className="flex border-b overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 border-b-2 transition-all whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'border-red-500 text-red-600 bg-red-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* ==================== CHECKLIST TAB ==================== */}
            {activeTab === 'checklist' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Checklist de Homologação</h3>
                  <Button onClick={runAllChecks} variant="outline" size="sm">
                    <Play className="w-4 h-4 mr-2" />
                    Executar Todos os Testes
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {checklistItems.map((item, idx) => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 font-mono text-sm w-6">{idx + 1}.</span>
                        <span className="text-gray-700">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {item.lastTest && (
                          <span className="text-xs text-gray-400">
                            {new Date(item.lastTest).toLocaleString('pt-BR')}
                          </span>
                        )}
                        <StatusBadge status={item.status} size="sm" />
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => runSingleCheck(item.id)}
                          disabled={item.status === 'RUNNING'}
                        >
                          <Play className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ==================== CONFIG TAB ==================== */}
            {activeTab === 'config' && (
              <div className="space-y-6">
                {!config?.configured ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                    <p className="text-gray-600">iFood não configurado para esta loja</p>
                    <p className="text-sm text-gray-400 mt-2">Configure na página de Integração iFood</p>
                  </div>
                ) : (
                  <>
                    {/* Credentials Info */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-red-500" />
                        Configuração Atual
                      </h4>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-gray-500">Client ID</label>
                          <p className="font-mono text-sm text-gray-800">{config.client_id || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Merchant ID</label>
                          <p className="font-mono text-sm text-gray-800">{config.merchant_id || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Client Secret</label>
                          <p className="font-mono text-sm text-gray-800">
                            {config.has_client_secret ? '••••••••••••' : 'Não configurado'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Status Autenticação</label>
                          <p className={`text-sm font-medium ${
                            config.auth_status === 'AUTHORIZED' ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            {config.auth_status}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Webhook</label>
                          <p className={`text-sm font-medium ${
                            config.webhook_enabled ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {config.webhook_enabled ? 'Habilitado' : 'Desabilitado'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Ambiente</label>
                          <p className="text-sm font-medium text-orange-600">Homologação</p>
                        </div>
                      </div>
                    </div>

                    {/* Test Buttons */}
                    <div className="flex gap-4">
                      <Button
                        onClick={handleTestCredentials}
                        disabled={testingCredentials}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        {testingCredentials ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Testar Credenciais
                      </Button>
                      
                      <Button
                        onClick={handleTestWebhook}
                        disabled={testingWebhook}
                        variant="outline"
                      >
                        {testingWebhook ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Testar Webhook
                      </Button>
                    </div>

                    {/* Token Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        Informações do Token
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-blue-600">Expira em:</span>
                          <span className="ml-2">
                            <TokenTimer expiresAt={config.token_expires_at} />
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-600">Última autenticação:</span>
                          <span className="ml-2 text-blue-900">
                            {config.last_auth_at ? new Date(config.last_auth_at).toLocaleString('pt-BR') : 'Nunca'}
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-600">Último poll:</span>
                          <span className="ml-2 text-blue-900">
                            {config.last_poll_at ? new Date(config.last_poll_at).toLocaleString('pt-BR') : 'Nunca'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ==================== EVENTS TAB ==================== */}
            {activeTab === 'events' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Monitor de Eventos (Webhook)</h3>
                  <Button onClick={loadWebhookEvents} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar
                  </Button>
                </div>

                {webhookEvents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum evento recebido via webhook</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Event ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Order ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Recebido</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Processado</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {webhookEvents.map(event => (
                          <tr key={event.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-xs text-gray-600">
                              {event.event_id?.slice(0, 8) || '-'}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-600">
                              {event.order_id?.slice(0, 8) || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                event.event_code === 'PLACED' ? 'bg-yellow-100 text-yellow-800' :
                                event.event_code === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                                event.event_code === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {event.event_code || event.event_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {new Date(event.received_at).toLocaleString('pt-BR')}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {event.processed_at ? new Date(event.processed_at).toLocaleString('pt-BR') : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <EventStatusBadge status={event.status} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedEvent(event)}
                                >
                                  <Eye className="w-3 h-3" />
                                </Button>
                                {event.status === 'FAILED' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleReprocessEvent(event.id)}
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Event Detail Modal */}
                {selectedEvent && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedEvent(null)}>
                    <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
                      <h4 className="text-lg font-semibold mb-4">Payload do Evento</h4>
                      <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-auto max-h-96">
                        {JSON.stringify(JSON.parse(selectedEvent.body_json || '{}'), null, 2)}
                      </pre>
                      <div className="flex justify-end mt-4">
                        <Button onClick={() => setSelectedEvent(null)}>Fechar</Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ==================== ORDERS TAB ==================== */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Pedidos iFood (Workflow)</h3>
                  <Button onClick={loadOrders} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar
                  </Button>
                </div>

                {orders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum pedido encontrado</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Header */}
                            <div className="flex items-center gap-3 mb-3">
                              <span className="font-mono text-lg font-bold text-gray-900">
                                #{order.ifood_display_id || order.ifood_order_id?.slice(0, 8)}
                              </span>
                              <OrderStatusBadge status={order.ifood_status} />
                              <span className={`px-2 py-1 rounded text-xs ${
                                order.order_type === 'DELIVERY' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {order.order_type === 'DELIVERY' ? (
                                  <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Delivery</span>
                                ) : (
                                  <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Retirada</span>
                                )}
                              </span>
                              {order.order_timing === 'SCHEDULED' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Agendado: {order.scheduled_datetime ? new Date(order.scheduled_datetime).toLocaleString('pt-BR') : '-'}
                                </span>
                              )}
                            </div>

                            {/* Customer & Payment Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              {order.customer_name && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <User className="w-4 h-4" />
                                  {order.customer_name}
                                </div>
                              )}
                              {order.customer_phone && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Phone className="w-4 h-4" />
                                  {order.customer_phone}
                                </div>
                              )}
                              {order.customer_document && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <FileText className="w-4 h-4" />
                                  CPF/CNPJ: {order.customer_document}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-gray-600">
                                <CreditCard className="w-4 h-4" />
                                {order.payment_method || 'Online'}
                                {order.payment_prepaid && <span className="text-green-600 text-xs">(Pago)</span>}
                                {order.payment_change_for > 0 && (
                                  <span className="text-orange-600 text-xs">
                                    Troco p/ R$ {order.payment_change_for.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 font-semibold text-green-600">
                                <DollarSign className="w-4 h-4" />
                                R$ {order.total?.toFixed(2)}
                              </div>
                            </div>

                            {/* Address */}
                            {order.delivery_address && (
                              <div className="flex items-start gap-2 text-sm text-gray-500 mb-3">
                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>
                                  {order.delivery_address.streetName}, {order.delivery_address.streetNumber}
                                  {order.delivery_address.neighborhood && ` - ${order.delivery_address.neighborhood}`}
                                </span>
                              </div>
                            )}

                            {/* Items with notes */}
                            {order.items && order.items.length > 0 && (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Itens:</p>
                                <div className="space-y-2">
                                  {order.items.map((item, idx) => (
                                    <div key={idx} className="text-sm">
                                      <div className="flex justify-between text-gray-600">
                                        <span>{item.quantity}x {item.name}</span>
                                        <span>R$ {item.totalPrice?.toFixed(2)}</span>
                                      </div>
                                      {item.observations && (
                                        <p className="text-xs text-orange-600 ml-4">
                                          Obs: {item.observations}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-2 ml-4">
                            {order.ifood_status === 'PLACED' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleConfirmOrder(order)}
                                  className="bg-blue-500 hover:bg-blue-600 text-white"
                                >
                                  Confirmar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleLoadCancellationReasons(order.ifood_order_id)}
                                  variant="outline"
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                >
                                  Cancelar
                                </Button>
                              </>
                            )}
                            {order.ifood_status === 'CONFIRMED' && (
                              <Button
                                size="sm"
                                onClick={() => handleDispatchOrder(order.ifood_order_id)}
                                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                              >
                                Despachar
                              </Button>
                            )}
                            {order.ifood_status === 'READY_TO_PICKUP' && order.order_type === 'DELIVERY' && (
                              <Button
                                size="sm"
                                onClick={() => handleDispatchOrder(order.ifood_order_id)}
                                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                              >
                                Despachar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRefreshOrderStatus(order.ifood_order_id)}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Atualizar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ==================== CANCEL TAB ==================== */}
            {activeTab === 'cancel' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Cancelamento (Motivos e Auditoria)</h3>
                
                {/* Regra Obrigatória */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Regra Obrigatória para Homologação</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Para cancelar um pedido, é <strong>obrigatório</strong> consultar os motivos de cancelamento 
                        disponíveis via API (<code className="bg-yellow-100 px-1 rounded">/cancellationReasons</code>) e selecionar um motivo válido.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fluxo de Cancelamento */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-3">Fluxo Correto de Cancelamento:</h4>
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <span className="flex items-center justify-center w-6 h-6 bg-blue-200 rounded-full text-blue-800 font-bold">1</span>
                    <span>Clique em "Carregar Motivos" para consultar a API</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="flex items-center justify-center w-6 h-6 bg-blue-200 rounded-full text-blue-800 font-bold">2</span>
                    <span>Selecione um motivo válido</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="flex items-center justify-center w-6 h-6 bg-blue-200 rounded-full text-blue-800 font-bold">3</span>
                    <span>Confirme o cancelamento</span>
                  </div>
                </div>

                {/* Orders that can be cancelled */}
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Pedidos Disponíveis para Cancelamento:
                  </h4>
                  <div className="space-y-3">
                    {orders.filter(o => ['PLACED', 'CONFIRMED', 'PREPARATION_STARTED'].includes(o.ifood_status)).map(order => (
                      <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="font-mono font-bold text-lg">#{order.ifood_display_id?.slice(0, 8)}</span>
                            <p className="text-sm text-gray-500">{order.customer_name}</p>
                          </div>
                          <OrderStatusBadge status={order.ifood_status} />
                          <div className="text-sm text-gray-500">
                            <span className="font-medium">R$ {order.total_price?.toFixed(2)}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleLoadCancellationReasons(order.ifood_order_id)}
                          disabled={loadingReasons && orderToCancel === order.ifood_order_id}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          {loadingReasons && orderToCancel === order.ifood_order_id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                              Carregando...
                            </>
                          ) : (
                            <>
                              <List className="w-4 h-4 mr-2" />
                              Carregar Motivos
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                    {orders.filter(o => ['PLACED', 'CONFIRMED', 'PREPARATION_STARTED'].includes(o.ifood_status)).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Ban className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Nenhum pedido disponível para cancelamento no momento</p>
                        <p className="text-sm text-gray-400 mt-1">Apenas pedidos com status PLACED, CONFIRMED ou PREPARATION_STARTED podem ser cancelados</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cancellation History */}
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Histórico de Cancelamentos (Auditoria):
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pedido</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Data/Hora</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {orders.filter(o => o.ifood_status === 'CANCELLED').map(order => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono font-bold">#{order.ifood_display_id?.slice(0, 8)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{order.customer_name || '-'}</td>
                            <td className="px-4 py-3 text-sm font-medium">R$ {order.total_price?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {order.cancelled_at || order.updated_at ? 
                                new Date(order.cancelled_at || order.updated_at).toLocaleString('pt-BR') : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                Cancelado
                              </span>
                            </td>
                          </tr>
                        ))}
                        {orders.filter(o => o.ifood_status === 'CANCELLED').length === 0 && (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              Nenhum cancelamento registrado ainda
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Checklist Status */}
                <div className={`p-4 rounded-lg border ${
                  orders.some(o => o.ifood_status === 'CANCELLED') 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-3">
                    {orders.some(o => o.ifood_status === 'CANCELLED') ? (
                      <>
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                        <div>
                          <h4 className="font-medium text-green-800">Requisito Atendido!</h4>
                          <p className="text-sm text-green-700">Você já realizou cancelamentos usando a API de motivos.</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Clock className="w-6 h-6 text-gray-500" />
                        <div>
                          <h4 className="font-medium text-gray-700">Requisito Pendente</h4>
                          <p className="text-sm text-gray-500">Cancele pelo menos um pedido usando os motivos da API para completar este item.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ==================== TAKEOUT TAB ==================== */}
            {activeTab === 'takeout' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Takeout / Código de Coleta</h3>
                
                {/* Takeout Orders */}
                <div className="space-y-4">
                  {orders.filter(o => o.order_type === 'TAKEOUT').length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum pedido TAKEOUT encontrado</p>
                    </div>
                  ) : (
                    orders.filter(o => o.order_type === 'TAKEOUT').map(order => (
                      <div key={order.id} className="bg-white border-2 border-purple-200 rounded-xl p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Header */}
                            <div className="flex items-center gap-3 mb-4">
                              <span className="font-mono text-xl font-bold text-gray-900">
                                #{order.ifood_display_id}
                              </span>
                              <OrderStatusBadge status={order.ifood_status} />
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                                <ShoppingBag className="w-3 h-3" /> Retirada
                              </span>
                            </div>

                            {/* Pickup Code - BIG */}
                            {order.pickup_code && (
                              <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6 mb-4 text-center">
                                <p className="text-sm text-purple-600 mb-2">Código de Coleta</p>
                                <p className="text-5xl font-bold text-purple-800 font-mono tracking-widest">
                                  {order.pickup_code}
                                </p>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="mt-2"
                                  onClick={() => {
                                    navigator.clipboard.writeText(order.pickup_code);
                                    toast.success('Código copiado!');
                                  }}
                                >
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copiar
                                </Button>
                              </div>
                            )}

                            {/* Customer Info */}
                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                              <div className="flex items-center gap-2 text-gray-600">
                                <User className="w-4 h-4" />
                                {order.customer_name || 'Cliente'}
                              </div>
                              {order.customer_phone && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Phone className="w-4 h-4" />
                                  {order.customer_phone}
                                </div>
                              )}
                            </div>

                            {/* Items */}
                            {order.items && order.items.length > 0 && (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Itens:</p>
                                <div className="space-y-1">
                                  {order.items.map((item, idx) => (
                                    <div key={idx} className="text-sm text-gray-600">
                                      {item.quantity}x {item.name}
                                      {item.observations && (
                                        <span className="text-orange-600 ml-2">({item.observations})</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-2 ml-4">
                            {order.ifood_status === 'CONFIRMED' && (
                              <Button
                                onClick={() => handleMarkReady(order.ifood_order_id, order.order_type)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                {order.order_type === "DELIVERY" ? "Despachar para Entrega" : "Pronto para Retirada"}
                              </Button>
                            )}
                            {order.ifood_status === 'READY_TO_PICKUP' && (
                              <div className="text-center p-4 bg-green-100 rounded-lg">
                                <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                <p className="text-green-700 font-medium">Aguardando Cliente</p>
                              </div>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.print()}
                            >
                              <Printer className="w-4 h-4 mr-2" />
                              Imprimir Comanda
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-auto">
              <h4 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Cancelar Pedido
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Pedido: <span className="font-mono font-bold">#{orderToCancel?.slice(0, 8)}</span>
              </p>
              
              {/* API Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-700">
                  <strong>API Consultada:</strong> GET /order/v1.0/orders/{'{order_id}'}/cancellationReasons
                </p>
              </div>
              
              {cancellationReasons.length === 0 ? (
                <div className="text-center py-6">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
                  <p className="text-gray-600 font-medium">Nenhum motivo disponível</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Este pedido não possui motivos de cancelamento válidos no momento.
                    Isso pode acontecer se o pedido já foi despachado ou está em trânsito.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Selecione um motivo ({cancellationReasons.length} disponíveis):
                  </p>
                  <div className="space-y-2 max-h-64 overflow-auto border rounded-lg p-2">
                    {cancellationReasons.map(reason => (
                      <button
                        key={reason.cancelCodeId}
                        onClick={() => setSelectedReason(reason)}
                        className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
                          selectedReason?.cancelCodeId === reason.cancelCodeId
                            ? 'border-red-500 bg-red-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{reason.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Código: <code className="bg-gray-100 px-1 rounded">{reason.cancelCodeId}</code>
                            </p>
                          </div>
                          {selectedReason?.cancelCodeId === reason.cancelCodeId && (
                            <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedReason(null);
                    setOrderToCancel(null);
                    setCancellationReasons([]);
                  }}
                >
                  Voltar
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                  onClick={handleCancelOrder}
                  disabled={!selectedReason || cancelling}
                >
                  {cancelling ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-2" />
                      Confirmar Cancelamento
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default IFoodHomologationPage;
